import React from 'react';
import { BadgeSection } from './BadgeSection';
import { FeaturedCategorySections } from './FeaturedCategorySections';
import { useBusinessesByBadge } from '@/hooks/useBusinessesByBadge';
import { useTranslation } from 'react-i18next';
import { Badge, BadgeConfig } from '@communitly/shared';

interface BadgeSectionsProps {
  badgeConfig: BadgeConfig[] | undefined;
  badges: Badge[] | undefined;
  currentCommunity: any;
  prepareServiceForCard: (service: any) => any;
  getBadgeIcon: (iconName: string) => React.ComponentType<any>;
  getBadgeDisplayName: (badgeSlug: string) => string;
  loading: boolean;
}

export const BadgeSections: React.FC<BadgeSectionsProps> = ({
  badgeConfig,
  badges,
  currentCommunity,
  prepareServiceForCard,
  getBadgeIcon,
  getBadgeDisplayName,
  loading
}) => {
  const { t } = useTranslation();

  // If no badge configuration, show default featured section
  if (!badgeConfig || badgeConfig.length === 0) {
    return (
      <DefaultFeaturedSection 
        currentCommunity={currentCommunity}
        prepareServiceForCard={prepareServiceForCard}
        getBadgeIcon={getBadgeIcon}
        loading={loading}
        badges={badges}
      />
    );
  }

  return (
    <>
      {badgeConfig.map((config) => {
        // Special handling for featured badge - use category-based sections
        if (config.badge_slug === 'featured') {
          return (
            <FeaturedCategorySections
              key={config.badge_slug}
              currentCommunity={currentCommunity}
              prepareServiceForCard={prepareServiceForCard}
              getBadgeIcon={getBadgeIcon}
              getBadgeDisplayName={getBadgeDisplayName}
              badges={badges || []}
            />
          );
        }
        
        // Normal badge handling
        return (
          <BadgeSectionWithData
            key={config.badge_slug}
            badgeSlug={config.badge_slug}
            currentCommunity={currentCommunity}
            prepareServiceForCard={prepareServiceForCard}
            getBadgeIcon={getBadgeIcon}
            getBadgeDisplayName={getBadgeDisplayName}
            badges={badges}
          />
        );
      })}
    </>
  );
};

interface BadgeSectionWithDataProps {
  badgeSlug: string;
  currentCommunity: any;
  prepareServiceForCard: (service: any) => any;
  getBadgeIcon: (iconName: string) => React.ComponentType<any>;
  getBadgeDisplayName: (badgeSlug: string) => string;
  badges: Badge[] | undefined;
}

const BadgeSectionWithData: React.FC<BadgeSectionWithDataProps> = ({
  badgeSlug,
  currentCommunity,
  prepareServiceForCard,
  getBadgeIcon,
  getBadgeDisplayName,
  badges
}) => {
  const { t } = useTranslation();
  const badge = badges?.find(b => b.slug === badgeSlug);
  
  const { data: businesses, isLoading } = useBusinessesByBadge({
    badgeSlug,
    communityId: currentCommunity?.id || '',
    limit: 3,
    enabled: !!currentCommunity?.id
  });

  if (!badge) {
    return null;
  }

  const Icon = getBadgeIcon(badge.icon);
  const displayName = getBadgeDisplayName(badgeSlug);
  
  return (
    <BadgeSection
      title={displayName}
      icon={Icon}
      services={businesses || []}
      loading={isLoading}
      emptyMessage={t('home.services.no.badge', { badge: displayName })}
      prepareServiceForCard={prepareServiceForCard}
      badgeColor={badge.color}
    />
  );
};

interface DefaultFeaturedSectionProps {
  currentCommunity: any;
  prepareServiceForCard: (service: any) => any;
  getBadgeIcon: (iconName: string) => React.ComponentType<any>;
  loading: boolean;
  badges?: Badge[];
}

const DefaultFeaturedSection: React.FC<DefaultFeaturedSectionProps> = ({
  currentCommunity,
  prepareServiceForCard,
  getBadgeIcon,
  loading,
  badges
}) => {
  const { t } = useTranslation();
  
  // Use the new FeaturedCategorySections component for consistency
  if (!loading && currentCommunity?.id) {
    return (
      <FeaturedCategorySections
        currentCommunity={currentCommunity}
        prepareServiceForCard={prepareServiceForCard}
        getBadgeIcon={getBadgeIcon}
        getBadgeDisplayName={(slug) => badges?.find(b => b.slug === slug)?.translatedName || slug}
        badges={badges || []}
      />
    );
  }

  // Fallback loading state
  const Icon = getBadgeIcon('Star') as import('lucide-react').LucideIcon;
  const featuredBadge = badges?.find(b => b.slug === 'featured');
  
  return (
    <BadgeSection
      title={t('home.featured.title')}
      icon={Icon}
      services={[]}
      loading={loading}
      emptyMessage={t('home.services.no.featured')}
      prepareServiceForCard={prepareServiceForCard}
      badgeColor={featuredBadge?.color}
    />
  );
};